import pluralize from "pluralize";

import {SelectOption} from "@/components/select/Select.types.ts";
import {IntervalFrequency, MonitoringServiceType} from "@/lib/types/checks.ts";
import {CheckFrequency} from "@/lib/types/devices.ts";

export const MINUTES_IN_DAY = 60 * 24;
export const MAX_INTERVAL_EXT_TXN = MINUTES_IN_DAY * 60;

export enum IntervalFieldType {
  NONE = "NONE",
  FIXED = "FIXED",
  INPUT_GROUP = "INPUT_GROUP",
}

export const getCheckMinInterval = (
  frequency: number | IntervalFrequency | undefined,
  limitCheckFrequency: CheckFrequency,
  fallbackMin: number
): number => {
  if (typeof frequency === "number") {
    return frequency;
  }
  if (frequency) {
    return frequency[limitCheckFrequency];
  }
  return fallbackMin;
};

export const getIntervalType = (forceInterval?: number): IntervalFieldType => {
  if (forceInterval === 0) {
    return IntervalFieldType.NONE;
  } else if (typeof forceInterval === "number" && !isNaN(forceInterval)) {
    return IntervalFieldType.FIXED;
  } else {
    return IntervalFieldType.INPUT_GROUP;
  }
};

export enum IntervalOption {
  MINUTE = "MINUTE",
  HOUR = "HOUR",
  DAY = "DAY",
}

export const IntervalOptions: SelectOption[] = [
  {value: IntervalOption.MINUTE, label: "Minutes"},
  {value: IntervalOption.HOUR, label: "Hours"},
  {value: IntervalOption.DAY, label: "Days"},
];

export const findIntervalOption = (count: number, forceOption?: IntervalOption) => {
  const intervalOption = forceOption || intervalOptionByMinute(count);
  const innerValue = intervalValueDisplay(count, intervalOption);
  const option = IntervalOptions.find(({value}) => value === intervalOption);
  if (innerValue === 1 && option) {
    return {...option, label: option.label.slice(0, -1)}; // Remove 's' for singular
  }
  return option;
};

export const findIntervalSuffix = (count: number, v: IntervalOption, inclusive?: boolean) => {
  return pluralize(v.toString().toLowerCase(), count, inclusive);
};

export const intervalValueDisplay = (minutes: number, v: IntervalOption): number => {
  switch (v) {
    case IntervalOption.DAY: {
      return minutes / 1440;
    }
    case IntervalOption.HOUR: {
      return minutes / 60;
    }
    default: {
      return minutes;
    }
  }
};

export const intervalMinutesFromDisplay = (n: number, v: IntervalOption): number => {
  switch (v) {
    case IntervalOption.DAY: {
      return n * 1440;
    }
    case IntervalOption.HOUR: {
      return n * 60;
    }
    default: {
      return n;
    }
  }
};

export const intervalOptionByMinute = (m: number): IntervalOption => {
  if (m >= MINUTES_IN_DAY && m % MINUTES_IN_DAY === 0) {
    return IntervalOption.DAY;
  }
  if (m >= 60 && m % 60 === 0) {
    return IntervalOption.HOUR;
  }

  return IntervalOption.MINUTE;
};

// ARBITRARY_HIGH_MAX for heartbeat
const ARBITRARY_HIGH_MAX = MINUTES_IN_DAY * 365;

export const getCheckMaxInterval = (checkType: MonitoringServiceType): number => {
  switch (checkType) {
    case MonitoringServiceType.HEARTBEAT: {
      return ARBITRARY_HIGH_MAX;
    }
    case MonitoringServiceType.PAGESPEED: {
      return MINUTES_IN_DAY * 7;
    }
    default: {
      return 60;
    }
  }
};

export const filterOutIntervalOptions = (options: SelectOption<string>[], max: number): SelectOption<string>[] => {
  if (max <= 60) {
    return options.filter((e) => [IntervalOption.MINUTE].includes(e.value as IntervalOption));
  }

  return options;
};
